import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useLocation } from "react-router-dom";

interface FiltersState {
  categories: string[];
  suburbs: string[];
  rating: string;
  verifiedOnly: boolean;
  showFavorites: boolean;
  topRatedOnly: boolean;
  featuredOnly: boolean;
}

interface ExploreFiltersHandlerProps {
  children: (props: {
    searchQuery: string;
    setSearchQuery: (query: string) => void;
    activeFilters: FiltersState;
    setActiveFilters: React.Dispatch<React.SetStateAction<FiltersState>>;
    appliedFilters: FiltersState;
    setAppliedFilters: React.Dispatch<React.SetStateAction<FiltersState>>;
    handleSearch: (e: React.FormEvent) => void;
    handleResetFilters: () => void;
    view: "grid" | "list";
    setView: (view: "grid" | "list") => void;
    sortOption: string;
    setSortOption: (option: string) => void;
  }) => React.ReactNode;
}

const getFiltersFromSearchParams = (searchParams: URLSearchParams) => ({
  searchQuery: searchParams.get("q") || "",
  filters: {
    categories: searchParams.getAll("category"),
    suburbs: searchParams.getAll("suburb"),
    rating: searchParams.get("rating") || "",
    verifiedOnly: searchParams.get("verified") === "true",
    showFavorites: searchParams.get("favorites") === "true",
    topRatedOnly: searchParams.get("topRated") === "true",
    featuredOnly: searchParams.get("featured") === "true",
  },
  sortOption: searchParams.get("sort") || "featured",
});

const ExploreFiltersHandler = ({ children }: ExploreFiltersHandlerProps) => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialState = getFiltersFromSearchParams(new URLSearchParams(location.search));
  
  // State variables
  const [searchQuery, setSearchQuery] = useState(initialState.searchQuery);
  const [view, setView] = useState<"grid" | "list">("grid");
  const [sortOption, setSortOption] = useState(initialState.sortOption);
  const [appliedFilters, setAppliedFilters] = useState(initialState.filters);
  const [activeFilters, setActiveFilters] = useState(initialState.filters);

  useEffect(() => {
    const nextState = getFiltersFromSearchParams(new URLSearchParams(location.search));
    setSearchQuery(nextState.searchQuery);
    setSortOption(nextState.sortOption);
    setAppliedFilters(nextState.filters);
    setActiveFilters(nextState.filters);
  }, [location.search]);

  // Process category from location state
  useEffect(() => {
    if (location.state?.category && !appliedFilters.categories.includes(location.state.category)) {
      const newCategories = [...appliedFilters.categories, location.state.category];
      setAppliedFilters(prev => ({
        ...prev,
        categories: newCategories
      }));
      setActiveFilters(prev => ({
        ...prev,
        categories: newCategories
      }));
      
      const params = new URLSearchParams(searchParams);
      params.append("category", location.state.category);
      setSearchParams(params);
    }
  }, [location.state, searchParams, setSearchParams, appliedFilters.categories]);

  // Update URL params based on filters
  useEffect(() => {
    const params = new URLSearchParams();
    
    if (searchQuery) params.set("q", searchQuery);
    
    appliedFilters.categories.forEach(category => {
      params.append("category", category);
    });
    
    appliedFilters.suburbs.forEach(suburb => {
      params.append("suburb", suburb);
    });
    
    if (appliedFilters.rating) params.set("rating", appliedFilters.rating);
    
    if (appliedFilters.verifiedOnly) params.set("verified", "true");
    
    if (appliedFilters.showFavorites) params.set("favorites", "true");
    
    if (appliedFilters.topRatedOnly) params.set("topRated", "true");
    
    if (appliedFilters.featuredOnly) params.set("featured", "true");

    if (sortOption) params.set("sort", sortOption);

    const nextSearch = params.toString();
    const currentSearch = new URLSearchParams(location.search).toString();

    if (nextSearch === currentSearch) {
      return;
    }
    
    setSearchParams(params, { replace: true });
  }, [appliedFilters, location.search, searchQuery, sortOption, setSearchParams]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setAppliedFilters({...activeFilters});
  };

  const handleResetFilters = useCallback(() => {
    setActiveFilters({
      categories: [],
      suburbs: [],
      rating: "",
      verifiedOnly: false,
      showFavorites: false,
      topRatedOnly: false,
      featuredOnly: false,
    });
    setAppliedFilters({
      categories: [],
      suburbs: [],
      rating: "",
      verifiedOnly: false,
      showFavorites: false,
      topRatedOnly: false,
      featuredOnly: false,
    });
    setSearchQuery("");
  }, []);

  return (
    <>
      {children({
        searchQuery,
        setSearchQuery,
        activeFilters,
        setActiveFilters,
        appliedFilters,
        setAppliedFilters,
        handleSearch,
        handleResetFilters,
        view,
        setView,
        sortOption,
        setSortOption,
      })}
    </>
  );
};

export default ExploreFiltersHandler;
